import React, { useEffect, useRef } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { RotateCwIcon, XIcon } from 'lucide-react';
import type { Entry } from '../types/raffle';
import { Confetti } from './Confetti';
interface WinnerOverlayProps {
  winner: Entry | null;
  remainingAfter: number;
  removeWinners: boolean;
  onClose: () => void;
  onSpinAgain: () => void;
}
export function WinnerOverlay({
  winner,
  remainingAfter,
  removeWinners,
  onClose,
  onSpinAgain
}: WinnerOverlayProps) {
  const againRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!winner) return;
    againRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [winner, onClose]);
  return <AnimatePresence data-id="element-332" data-mp="12nycmcv4c1ff">
      {winner && <motion.div className="fixed inset-0 z-[60] flex items-center justify-center overflow-y-auto bg-ink/95 px-6 py-10" role="dialog" aria-modal="true" aria-labelledby="winner-name" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.22,
      ease: [0.23, 1, 0.32, 1]
    }} data-id="element-333" data-mp="17ocbb4atxz0s">
          <Confetti data-id="element-334" data-mp="2q9jm2b9mkyny" />
          <motion.div className="relative my-auto w-full max-w-5xl text-center" initial={{
        opacity: 0,
        scale: 0.96,
        y: 10
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.98
      }} transition={{
        duration: 0.28,
        ease: [0.23, 1, 0.32, 1]
      }} data-id="element-335" data-mp="2q88jufvtvyyy">
            <p className="text-sm font-semibold uppercase tracking-[0.32em] text-gold sm:text-base" data-id="element-336" data-mp="2catpqxumr30n">
              Winner
            </p>
            <h2 id="winner-name" className="mt-6 break-words font-display text-[clamp(3rem,11vw,8rem)] leading-[1.02] text-white" data-id="element-337" data-mp="ahvr2le69g7s">
              {winner.name}
            </h2>
            <p className="mt-6 text-sm text-muted sm:text-base" data-id="element-338" data-mp="1zxzev2lbns7n">
              {removeWinners ? remainingAfter > 0 ? `Removed from the wheel — ${remainingAfter} name${remainingAfter === 1 ? '' : 's'} left.` : 'That was the last name on the wheel.' : 'Still on the wheel and can win again.'}
            </p>

            <div className="mt-10 flex flex-col items-center justify-center gap-3 sm:mt-12 sm:flex-row sm:gap-4" data-id="element-339" data-mp="2ioqz4z6ul30t">
              <button ref={againRef} type="button" onClick={onSpinAgain} disabled={removeWinners && remainingAfter < 2} className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-gold px-9 py-4 text-base font-bold uppercase tracking-[0.12em] text-ink transition-[background-color,transform] duration-150 ease-emphasis hover:bg-[#F0C154] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-gold active:scale-[0.98] disabled:cursor-not-allowed disabled:bg-line disabled:text-muted sm:w-auto" data-id="element-340" data-mp="2nnaiqsxyy2q0">
                <RotateCwIcon className="h-4 w-4" aria-hidden="true" data-id="element-341" data-mp="purd9hmwh17u" />
                Spin again
              </button>
              <button type="button" onClick={onClose} className="inline-flex w-full items-center justify-center gap-2 rounded-full border border-muted/70 px-9 py-4 text-base font-semibold text-white transition-colors duration-150 ease-emphasis hover:border-gold hover:text-gold focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-gold sm:w-auto" data-id="element-342" data-mp="zq1cxyptswig">
                <XIcon className="h-4 w-4" aria-hidden="true" data-id="element-343" data-mp="gyrz5d90vll2" />
                Close
              </button>
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}